import React, { useState } from "react";
import { LEAVE_APPROVAL_ROLES } from "../../../../lib/leaveApprovalWorkflow";

/**
 * Reusable leave approval workflow editor (group-level, per leave-type, etc.).
 * Expects workflow shape from defaultLeaveGroupWorkflow / normalizeLeaveGroupWorkflow.
 */
const LeaveWorkflowEditor = ({
  darkMode,
  workflow,
  onChange,
  showTitle = true,
  autoApprovedMessage,
}) => {
  const [workflowStepsTab, setWorkflowStepsTab] = useState("approval");
  const inputCls = `w-full p-2 border rounded-none text-sm ${
    darkMode ? "bg-gray-800 border-gray-500 text-white" : "bg-white border-gray-300"
  }`;
  const labelCls = `block text-sm mb-1 ${darkMode ? "text-gray-300" : "text-gray-700"}`;
  const stepLabelCls = `block text-xs mb-1 ${darkMode ? "text-gray-300" : "text-gray-700"}`;
  const stepsKey =
    workflowStepsTab === "approval" ? "workflowSteps" : "cancellationWorkflowSteps";
  const steps = Array.isArray(workflow[stepsKey]) ? workflow[stepsKey] : [];
  const showWorkflowSteps =
    workflow.approvalMethod === "workflow" ||
    (workflow.approvalMethod === "on_instance" && workflow.needWorkflowSteps);

  const setWorkflow = (patch) => onChange({ ...workflow, ...patch });

  const updateStep = (index, patch) => {
    const next = steps.map((step, i) => (i === index ? { ...step, ...patch } : step));
    setWorkflow({ [stepsKey]: next });
  };

  const addStep = () => {
    if (steps.length >= 5) return;
    setWorkflow({
      [stepsKey]: [
        ...steps,
        {
          role:
            LEAVE_APPROVAL_ROLES[Math.min(steps.length, LEAVE_APPROVAL_ROLES.length - 1)]
              ?.value || "hr",
          slaDays: 3,
          notifyOnly: false,
        },
      ],
    });
  };

  const removeStep = (index) => {
    setWorkflow({ [stepsKey]: steps.filter((_, i) => i !== index) });
  };

  const roleOptions = LEAVE_APPROVAL_ROLES.filter((role) =>
    ["manager", "hr", "management", "reporting_manager"].includes(role.value)
  );

  return (
    <div
      className={`p-3 border rounded-none ${
        darkMode ? "border-gray-600 bg-gray-700" : "border-gray-200 bg-gray-50"
      }`}
    >
      {showTitle && (
        <h4 className={`text-sm font-semibold mb-2 ${darkMode ? "text-white" : "text-gray-800"}`}>
          Workflow
        </h4>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <div>
          <label className={labelCls}>Approval Method</label>
          <select
            value={workflow.approvalMethod || "workflow"}
            onChange={(e) => setWorkflow({ approvalMethod: e.target.value })}
            className={inputCls}
          >
            <option value="auto_approved">Auto approved</option>
            <option value="workflow">Through workflow</option>
            <option value="on_instance">On instance</option>
          </select>
        </div>
        {showWorkflowSteps && (
          <div>
            <label className={labelCls}>Reminder days before leave date</label>
            <input
              type="number"
              min={0}
              value={workflow.reminderDays ?? 7}
              onChange={(e) =>
                setWorkflow({ reminderDays: Number(e.target.value) || 0 })
              }
              className={inputCls}
            />
          </div>
        )}
      </div>

      {workflow.approvalMethod === "on_instance" && (
        <label className="flex items-center gap-2 mb-3 text-sm">
          <input
            type="checkbox"
            checked={!!workflow.needWorkflowSteps}
            onChange={(e) => setWorkflow({ needWorkflowSteps: e.target.checked })}
            className="rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35"
          />
          Use workflow steps
        </label>
      )}

      {showWorkflowSteps && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
            <label className="flex items-center text-sm sm:col-span-2 lg:col-span-1 lg:mt-6">
              <input
                type="checkbox"
                checked={!!workflow.workflowParallelApproval}
                onChange={(e) =>
                  setWorkflow({ workflowParallelApproval: e.target.checked })
                }
                className="mr-2 rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35"
              />
              Parallel approval option
            </label>
            <div>
              <label className={labelCls}>Escalation after (days)</label>
              <input
                type="number"
                min={1}
                value={workflow.workflowEscalationAfterDays ?? 3}
                onChange={(e) =>
                  setWorkflow({
                    workflowEscalationAfterDays: Number(e.target.value) || 3,
                  })
                }
                className={inputCls}
              />
            </div>
          </div>

          <div className="flex gap-2 mb-3">
            <button
              type="button"
              onClick={() => setWorkflowStepsTab("approval")}
              className={`px-3 py-1 text-xs rounded-none ${
                workflowStepsTab === "approval"
                  ? "rh-btn-primary"
                  : "bg-gray-200 text-gray-700"
              }`}
            >
              Approval of new request
            </button>
            <button
              type="button"
              onClick={() => setWorkflowStepsTab("cancellation")}
              className={`px-3 py-1 text-xs rounded-none ${
                workflowStepsTab === "cancellation"
                  ? "rh-btn-primary"
                  : "bg-gray-200 text-gray-700"
              }`}
            >
              Cancellation
            </button>
          </div>

          <div className="space-y-3">
            {steps.map((step, index) => (
              <div
                key={`${stepsKey}-${index}`}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[minmax(0,1.4fr)_7rem_auto_auto] gap-3 items-end"
              >
                <div className="min-w-0 sm:col-span-2 lg:col-span-1">
                  <label className={stepLabelCls}>Role</label>
                  <select
                    value={step.role || "reporting_manager"}
                    onChange={(e) => updateStep(index, { role: e.target.value })}
                    className={inputCls}
                  >
                    {roleOptions.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={stepLabelCls}>SLA Days</label>
                  <input
                    type="number"
                    min={1}
                    value={step.slaDays ?? 3}
                    onChange={(e) =>
                      updateStep(index, { slaDays: Number(e.target.value) || 3 })
                    }
                    className={inputCls}
                  />
                </div>
                <label className="flex items-center text-sm whitespace-nowrap pb-2">
                  <input
                    type="checkbox"
                    checked={!!step.notifyOnly}
                    onChange={(e) => updateStep(index, { notifyOnly: e.target.checked })}
                    className="mr-2 rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35"
                  />
                  Notify only
                </label>
                <button
                  type="button"
                  onClick={() => removeStep(index)}
                  className="px-3 py-2 text-xs rounded-none bg-red-100 text-red-700 hover:bg-red-200 whitespace-nowrap justify-self-start lg:justify-self-end"
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addStep}
              disabled={steps.length >= 5}
              className="px-3 py-1.5 text-xs rh-btn-primary rounded-none hover:opacity-95 disabled:opacity-50"
            >
              + Add Step
            </button>
          </div>
        </>
      )}

      {workflow.approvalMethod === "auto_approved" && autoApprovedMessage && (
        <p className={`text-sm mt-2 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
          {autoApprovedMessage}
        </p>
      )}
    </div>
  );
};

export default LeaveWorkflowEditor;
